import { createContext, FC, PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { LocationData } from '../@types/common.types';
import { getUserLocationData } from '../utils/location';
import { DEFAULT_COUNTRY } from '../constants/common.constants';

const DEFAULT_LOCATION_DATA: LocationData = {
    ip: '',
    asn: '',
    as_name: '',
    as_domain: '',
    country_code: DEFAULT_COUNTRY,
    country: 'United States',
    continent_code: 'NA',
    continent: 'North America',
};

export interface LocationDetectionContextValue {
    locationData: LocationData | null;
    loading: boolean;
    error: Error | null;
    refetch: () => Promise<void>;
}

export const LocationDetectionContext = createContext<LocationDetectionContextValue>({
    locationData: null,
    loading: false,
    error: null,
    refetch: async () => {},
});

export const LocationDetectionProvider: FC<PropsWithChildren> = ({ children }) => {
    const [locationData, setLocationData] = useState<LocationData | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<Error | null>(null);

    const fetchLocation = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getUserLocationData();
            setLocationData(data ?? DEFAULT_LOCATION_DATA);
        } catch (err) {
            setError(err as Error);
            setLocationData(DEFAULT_LOCATION_DATA);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void fetchLocation();
    }, [fetchLocation]);

    return (
        <LocationDetectionContext.Provider
            value={{ locationData, loading, error, refetch: fetchLocation }}
        >
            {children}
        </LocationDetectionContext.Provider>
    );
};
